import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppSkinProps } from '../../../types/decisions';
import Modal from '../../modal/Modal';
import ResizableDivs from '../../resizableDivs/ResizableDivs';
import CriteriaSetting from '../app-components/CriteriaSetting';
import AppResults from '../app-components/AppResults/AppResults';
import Loading from '../../loading/Loading';
import { useAppConfig } from '../../../context/app-configs-context';
import AppInfo from '../app-components/AppInfo';
import { getCriteriaFieldsToDisplayInCriteria, getCriteriaFieldsToDisplayInResults } from '../../../utils';

const AppSkin5 = ({
    results,
    onRerank,
    onRerankMany,
    criteriaLoading,
    criteriaData,
    onSliderChange,
    onSliderBulkChange,
    onCriteriaApply,
    resultLoading,
    onPageChange,
    pageInfo,
    onTradeoffValueChange,
    fieldInitialValues,
    selectedProductIds,
    setSelectedProductIds,
}: AppSkinProps) => {
    const {
        appConfigs: {
            inferenceRank,
            criteriaSettings: { popup },
        },
    } = useAppConfig();
    const [ modalContent, setModalContent ] = useState<React.ReactElement>(<></>);
    const [ open, setOpen ] = useState(false);
    const modalCallbackRef = useRef<(() => void) | undefined>();
    const criteriaChangedOnceRef = useRef<{
        [key: string]: boolean
    }>({});
    const shouldShowRankByManyCheckbox = inferenceRank === 'ThumbsUp';

    const displayFieldsIds: string[] = useMemo(() => {
        return getCriteriaFieldsToDisplayInResults(criteriaData.fields)
        .map(item => item.id);
    }, [ criteriaData.fields ]);

    useEffect(() => {
        if (modalCallbackRef.current && !open) {
            modalCallbackRef.current();
            modalCallbackRef.current = undefined;
        }
    }, [ open ]);

    const onAutoPopupCheck = (newImportance: number, id: string, callback?: () => void) => {
        if (!popup.show || newImportance < popup.threshold) {
            return callback?.();
        }

        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            onCriteriaSettingsOpen(id);
            modalCallbackRef.current = callback;
        } else {
            callback?.();
        }
    };

    const handleClose = () => {
        setModalContent(<></>);
        setOpen(false);
    };

    const onCriteriaSettingsOpen = (id: string) => {
        const field = criteriaData.fields.find(f => f.id === id);
        if (!field) {
            return;
        }
        setOpen(true);
        setModalContent(
            <CriteriaSetting
                field={field}
                onSliderChange={onSliderChange}
                onTradeoffValueChange={onTradeoffValueChange}
                fieldInitialValues={fieldInitialValues}
                handleModalClose={handleClose}
                areCTAsDisplayed
            />,
        );
    };

    return (
        <div className="application skin6">
            {criteriaLoading ? (
                <Loading/>
            ) : (
                <>
                    <AppInfo
                        icon={criteriaData.icon}
                        name={criteriaData.name}
                        description={criteriaData.description}
                    />
                    <div className="application-details">
                        <div className="app-criteria" id="app-criteria">
                            {criteriaLoading ? (
                                <Loading/>
                            ) : (
                                <div className="app-criteria-top-slider">
                                    <ResizableDivs
                                        criteriaFields={getCriteriaFieldsToDisplayInCriteria(criteriaData.fields)}
                                        initialCriteriaFields={criteriaData.fields}
                                        onCriteriaApply={onCriteriaApply}
                                        onTradeoffValueChange={onTradeoffValueChange}
                                        onSliderBulkChange={onSliderBulkChange}
                                        onCriteriaSettingsOpen={onCriteriaSettingsOpen}
                                        onAutoPopupCheck={onAutoPopupCheck}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="app-results skin5-products">
                            {resultLoading ? (
                                <Loading/>
                            ) : (
                                <AppResults
                                    results={results}
                                    pageInfo={pageInfo}
                                    onPageChange={onPageChange}
                                    onRerank={onRerank}
                                    onRerankMany={onRerankMany}
                                    shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                                    selectedProductIds={selectedProductIds}
                                    setSelectedProductIds={setSelectedProductIds}
                                    allowedFieldsIds={displayFieldsIds}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
            <Modal isOpen={open} handleClose={handleClose}>
                {modalContent}
            </Modal>
        </div>
    );
};

export default AppSkin5;